import { formattedPlayerName } from "../../../Utils/name_utils";

/**
 * Formats a player name using the team's naming convention
 * @param {Object} player - Player object with first_name and last_name
 * @param {string} teamName - Team display name
 * @returns {string} Formatted player name
 */
export function formatPlayerName(player, teamName) {
	if (!player || !teamName) return "";
	return formattedPlayerName(
		teamName,
		`${player.first_name || ""} ${player.last_name || ""}`.trim()
	);
}

/**
 * Normalizes an entity to a common format for autocomplete
 * @param {*} entity - The entity object or value
 * @param {string} type - Entity type: 'players', 'metrics', 'positions', 'playerGroups', 'sessions', 'treatments', 'treatmentApplicationTime'
 * @param {string} teamName - Team name for formatting player names
 * @returns {Object} Normalized entity with { label, value, type, ...originalData }
 */
export function normalizeEntity(entity, type, teamName = "") {
	switch (type) {
		case "players":
			return {
				label: formatPlayerName(entity, teamName),
				value: formatPlayerName(entity, teamName),
				type: "player",
				player_id: entity.player_id,
				first_name: entity.first_name,
				last_name: entity.last_name,
				position: entity.position,
				number: entity.number,
				...entity,
			};

		case "metrics":
			return {
				label: entity.name || entity.key || String(entity),
				value: entity.name || entity.key || String(entity),
				type: "metric",
				key: entity.key,
				name: entity.name,
				...entity,
			};

		case "positions":
			// Positions can be strings or objects
			const positionValue = typeof entity === "string" ? entity : entity.label || entity.key || String(entity);
			return {
				label: positionValue,
				value: positionValue,
				type: "position",
				...(typeof entity === "object" ? entity : { position: entity }),
			};

		case "playerGroups":
			return {
				label: entity.name || String(entity),
				value: entity.name || String(entity),
				type: "playerGroup",
				player_group_id: entity.player_group_id,
				name: entity.name,
				...entity,
			};

	case "sessions":
		const sessionValue = typeof entity === "string" ? entity : String(entity);
		return {
			label: sessionValue,
			value: sessionValue,
			type: "session",
		};

	case "treatments":
		return {
			label: entity.name || String(entity),
			value: entity.name || String(entity),
			type: "treatment",
			name: entity.name,
			color: entity.color,
			...entity,
		};

	case "treatmentApplicationTime":
		return {
			label: entity.name || String(entity),
			value: entity.name || String(entity),
			type: "treatmentApplicationTime",
			name: entity.name,
			...entity,
		};

	default:
		return {
			label: String(entity),
			value: String(entity),
			type: type,
			...entity,
		};
	}
}

/**
 * Extracts entities from Redux team state based on requested entity types
 * @param {Object} teamState - Redux team state
 * @param {Array<string>} entityTypes - Array of entity types to include: ['players', 'metrics', 'positions', 'playerGroups', 'sessions', 'treatments', 'treatmentApplicationTime']
 * @returns {Array<Object>} Array of normalized entities ready for autocomplete
 */
export function extractEntitiesFromRedux(teamState, entityTypes = []) {
	if (!teamState) return [];

	const entities = [];
	const teamName = teamState.info?.display_name || "";
	// Track seen metric names/keys to avoid duplicates
	const seenMetrics = new Set();
	const seenSessions = new Set();

	entityTypes.forEach((type) => {
		switch (type) {
			case "players":
				if (Array.isArray(teamState.players)) {
					teamState.players.forEach((player) => {
						if (player) {
							entities.push(normalizeEntity(player, "players", teamName));
						}
					});
				}
				break;

			case "metrics":
				// all_metric_types is an object with keys: gps, wellness, forceplate, testing, nutrition, cognition
				// Each key contains an array of metric objects with a 'name' property
				if (teamState.all_metric_types) {
					Object.keys(teamState.all_metric_types).forEach((metricType) => {
						const metricArray = teamState.all_metric_types[metricType];
						if (Array.isArray(metricArray)) {
							metricArray.forEach((metric) => {
								if (metric && metric.name) {
									const normalized = normalizeEntity(metric, "metrics", teamName);
									// Use name or key as unique identifier
									const metricId = normalized.name || normalized.key || normalized.label;
									if (metricId && !seenMetrics.has(metricId)) {
										seenMetrics.add(metricId);
										entities.push(normalized);
									}
								}
							});
						}
					});
				}
				break;

			case "positions":
				// Check both positions and allPositions arrays
				const positions = teamState.positions || teamState.allPositions || [];
				if (Array.isArray(positions)) {
					positions.forEach((position) => {
						if (position) {
							entities.push(normalizeEntity(position, "positions", teamName));
						}
					});
				}
				break;

			case "playerGroups":
				if (Array.isArray(teamState.playerGroups)) {
					teamState.playerGroups.forEach((group) => {
						if (group) {
							entities.push(normalizeEntity(group, "playerGroups", teamName));
						}
					});
				}
				break;

		case "sessions":
			if (Array.isArray(teamState.sessions)) {
				teamState.sessions.forEach((session) => {
					if (session && !seenSessions.has(session)) {
						seenSessions.add(session);
						entities.push(normalizeEntity(session, "sessions", teamName));
					}
				});
			}
			break;

		case "treatments":
			if (Array.isArray(teamState.misc?.treatment_types)) {
				teamState.misc.treatment_types.forEach((treatment) => {
					if (treatment) {
						entities.push(normalizeEntity(treatment, "treatments", teamName));
					}
				});
			}
			break;

		case "treatmentApplicationTime":
			if (Array.isArray(teamState.misc?.treatment_applied_time)) {
				teamState.misc.treatment_applied_time.forEach((item) => {
					if (item) {
						entities.push(normalizeEntity(item, "treatmentApplicationTime", teamName));
					}
				});
			}
			break;

		default:
			console.warn(`Unknown entity type: ${type}`);
		}
	});

	return entities;
}

